import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, Loader2, Stethoscope } from "lucide-react";

const CARE_SETTINGS = [
  "Hospital / Health System",
  "Senior Living / Assisted Living",
  "Skilled Nursing / Rehab",
  "Memory Care",
  "Home & Community Care",
  "Clinic / Outpatient",
  "Behavioral Health",
  "Other Care Setting",
];

const NEEDS = [
  "Environmental Services / Cleaning",
  "Meal & Tray Delivery",
  "Supply & Linen Transport",
  "Lab / Pharmacy Runs",
  "Resident Engagement & Companionship",
  "Front Desk / Wayfinding",
  "Disinfection",
  "Assistive Mobility",
];

const schema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(120),
  email: z.string().trim().email("Please enter a valid work email").max(255),
  phone: z.string().trim().max(40).optional(),
  organization: z.string().trim().min(1, "Please enter your organization").max(200),
});

const field = "mt-1.5 bg-background/60 border-primary/30";

const HealthcareAssessmentForm = () => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [setting, setSetting] = useState("");
  const [needs, setNeeds] = useState<string[]>([]);
  const [beds, setBeds] = useState("");
  const [facilitySize, setFacilitySize] = useState("");
  const [shifts, setShifts] = useState("");
  const [headcount, setHeadcount] = useState("");
  const [repetitiveHours, setRepetitiveHours] = useState("");
  const [laborCost, setLaborCost] = useState("");
  const [notes, setNotes] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", organization: "" });

  const toggleNeed = (n: string) =>
    setNeeds((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setting) {
      toast.error("Select your care setting");
      return;
    }
    if (needs.length === 0) {
      toast.error("Select at least one area you want to automate");
      return;
    }
    const parsed = schema.safeParse(contact);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Please complete the form");
      return;
    }

    setLoading(true);
    const environment = [
      setting,
      beds && `${beds} beds/residents`,
      facilitySize && `${facilitySize}`,
      shifts && `Shifts: ${shifts}`,
      notes && `Notes: ${notes}`,
    ]
      .filter(Boolean)
      .join(" | ")
      .slice(0, 500);

    const { error } = await supabase.from("roi_leads").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone || null,
      business_name: parsed.data.organization,
      industry: "Healthcare & Senior Care",
      interest: `N.E.I.L. Healthcare Assessment — ${needs.join(", ")}`.slice(0, 500),
      facility_size: environment || null,
      monthly_labor_cost: laborCost ? Number(laborCost) : null,
      repetitive_hours: repetitiveHours ? Number(repetitiveHours) : null,
    });
    setLoading(false);

    if (error) {
      toast.error("Could not submit. Please try again or call (336) 962-4539.");
      return;
    }
    setDone(true);
    toast.success("Assessment submitted — your quote is on the way.");
  };

  if (done) {
    return (
      <Card className="mx-auto max-w-3xl border-primary/25 bg-card/60 p-8 text-center">
        <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
        <h3 className="font-orbitron mt-5 text-2xl font-bold text-foreground">ASSESSMENT RECEIVED</h3>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
          A Neil AI healthcare specialist will review your operation details and return a scoped
          Robotics-as-a-Service quote, typically within one business day. If additional discovery is
          required, we'll schedule a site assessment to complete the N.E.I.L.™ process.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          Need it sooner? Call <a className="text-primary underline" href="tel:+13369624539">(336) 962-4539</a>.
        </p>
      </Card>
    );
  }

  return (
    <Card className="mx-auto max-w-3xl border-primary/20 bg-card/60 p-6 md:p-8">
      <div className="flex items-center gap-2">
        <Stethoscope className="h-5 w-5 text-[#D6A84F]" />
        <p className="text-xs font-bold tracking-[0.28em] text-[#D6A84F]">HEALTHCARE & SENIOR CARE</p>
      </div>
      <h3 className="font-orbitron mt-3 text-2xl font-bold text-foreground md:text-3xl">
        Get Your N.E.I.L.™ Assessment & Quote
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Share your operation details. We build the labor baseline first, then return a scoped RaaS quote —
        no robot is recommended before discovery.
      </p>

      <form onSubmit={submit} className="mt-7 space-y-6">
        <fieldset>
          <legend className="text-sm font-semibold text-foreground">Care setting *</legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {CARE_SETTINGS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSetting(s)}
                aria-pressed={setting === s}
                className={`min-h-12 rounded-2xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
                  setting === s
                    ? "border-primary bg-primary/15 text-foreground"
                    : "border-[hsl(var(--line))] bg-background/50 text-muted-foreground hover:text-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-sm font-semibold text-foreground">What do you want to automate? *</legend>
          <div className="mt-3 flex flex-wrap gap-2">
            {NEEDS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => toggleNeed(n)}
                aria-pressed={needs.includes(n)}
                className={`min-h-10 rounded-full border px-4 py-2 text-sm transition-colors ${
                  needs.includes(n)
                    ? "border-[#D6A84F] bg-[#D6A84F]/15 text-foreground"
                    : "border-[hsl(var(--line))] bg-background/50 text-muted-foreground hover:text-foreground"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="hc-beds">Beds / residents served</Label>
            <Input id="hc-beds" value={beds} onChange={(e) => setBeds(e.target.value)} placeholder="e.g. 120" className={field} maxLength={60} />
          </div>
          <div>
            <Label htmlFor="hc-size">Facility size</Label>
            <Input id="hc-size" value={facilitySize} onChange={(e) => setFacilitySize(e.target.value)} placeholder="e.g. 85,000 sq ft, 3 floors" className={field} maxLength={120} />
          </div>
          <div>
            <Label htmlFor="hc-shifts">Shifts / operating hours</Label>
            <Input id="hc-shifts" value={shifts} onChange={(e) => setShifts(e.target.value)} placeholder="e.g. 3 shifts, 24/7" className={field} maxLength={80} />
          </div>
          <div>
            <Label htmlFor="hc-head">Staff on this work</Label>
            <Input id="hc-head" value={headcount} onChange={(e) => setHeadcount(e.target.value)} placeholder="e.g. 14 EVS techs" className={field} maxLength={80} />
          </div>
          <div>
            <Label htmlFor="hc-hours">Repetitive hours per week</Label>
            <Input id="hc-hours" type="number" min={0} value={repetitiveHours} onChange={(e) => setRepetitiveHours(e.target.value)} className={field} />
          </div>
          <div>
            <Label htmlFor="hc-cost">Monthly labor / operating cost ($)</Label>
            <Input id="hc-cost" type="number" min={0} value={laborCost} onChange={(e) => setLaborCost(e.target.value)} className={field} />
          </div>
        </div>

        <div>
          <Label htmlFor="hc-notes">Environment details (elevators, corridors, infection control, EHR/nurse-call systems)</Label>
          <Textarea id="hc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={field} maxLength={1000} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="hc-name">Name *</Label>
            <Input id="hc-name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={field} maxLength={120} />
          </div>
          <div>
            <Label htmlFor="hc-email">Work email *</Label>
            <Input id="hc-email" type="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={field} maxLength={255} />
          </div>
          <div>
            <Label htmlFor="hc-phone">Phone</Label>
            <Input id="hc-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={field} maxLength={40} />
          </div>
          <div>
            <Label htmlFor="hc-org">Organization *</Label>
            <Input id="hc-org" required value={contact.organization} onChange={(e) => setContact({ ...contact, organization: e.target.value })} className={field} maxLength={200} />
          </div>
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          By submitting, you consent to Neil AI Global Solutions contacting you about this assessment and
          quote. We do not sell your information. Quotes include transparent setup fees; projections are
          based on the data you provide and estimates are labeled as estimates.
        </p>

        <Button
          type="submit"
          disabled={loading}
          size="lg"
          className="h-12 w-full rounded-full bg-gradient-to-r from-[hsl(43_75%_55%)] to-[hsl(43_85%_65%)] font-semibold text-black hover:opacity-95"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {loading ? "Submitting…" : "GET MY ASSESSMENT & QUOTE"}
        </Button>
      </form>
    </Card>
  );
};

export default HealthcareAssessmentForm;
